import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, RefreshCw, Link2, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { normalizeAlias, invalidateDocIndex } from "@/lib/docLinks";

type AuditRow = {
  id: string;
  doc_id: string;
  phrase: string;
  phrase_context: string | null;
  phrase_kind: string;
  status: string;
  resolved_doc_id: string | null;
  candidates: { doc_id: string; title: string; collection: string; slug: string | null }[] | null;
  audited_at: string;
  dismissed: boolean;
};

type DocLite = { doc_id: string; title: string };

const STATUS_ORDER = ["unresolved", "ambiguous", "target_unpublished", "dead_anchor", "resolved"] as const;

const STATUS_LABEL: Record<string, string> = {
  resolved: "Resolved",
  unresolved: "Unresolved",
  ambiguous: "Ambiguous",
  target_unpublished: "Target unpublished",
  dead_anchor: "Dead anchor",
};

export default function AdminLinkHealth() {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [docs, setDocs] = useState<Map<string, DocLite>>(new Map());
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [filter, setFilter] = useState<string>("unresolved");
  const [busy, setBusy] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    const [{ data: audit }, { data: docList }] = await Promise.all([
      supabase.from("doc_link_audit" as any).select("*").eq("dismissed", false).order("audited_at", { ascending: false }),
      supabase.from("sop_documents" as any).select("doc_id,title"),
    ]);
    setRows((audit as any[]) || []);
    const m = new Map<string, DocLite>();
    for (const d of (docList as any[]) || []) m.set(d.doc_id, { doc_id: d.doc_id, title: d.title });
    setDocs(m);
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  async function runAudit() {
    setRunning(true);
    try {
      const { data, error } = await supabase.functions.invoke("link-audit");
      if (error) throw error;
      toast.success(`Audited ${data?.total ?? 0} references`);
      await load();
    } catch (e: any) {
      toast.error(e?.message || "Audit failed");
    } finally {
      setRunning(false);
    }
  }

  async function attachAlias(row: AuditRow, targetDocId: string) {
    setBusy(row.id);
    try {
      // Derive alias from the inner phrase.
      let alias = normalizeAlias(row.phrase);
      // For "the X doc/guide" phrases, strip the wrapper.
      const m = row.phrase.toLowerCase().match(/^(?:the|see)\s+(.+?)\s+(?:docs?|guides?)$/);
      if (m) alias = normalizeAlias(m[1]);
      if (!alias) { toast.error("Could not derive alias"); return; }

      const { data: target, error: tErr } = await supabase
        .from("sop_documents" as any)
        .select("id,link_aliases")
        .eq("doc_id", targetDocId)
        .maybeSingle();
      if (tErr || !target) throw tErr || new Error("Target not found");
      const existing: string[] = ((target as any).link_aliases as string[]) || [];
      if (!existing.includes(alias)) {
        const { error: uErr } = await supabase
          .from("sop_documents" as any)
          .update({ link_aliases: [...existing, alias] })
          .eq("id", (target as any).id);
        if (uErr) throw uErr;
      }
      await supabase.from("doc_link_audit" as any)
        .update({ status: "resolved", resolved_doc_id: targetDocId })
        .eq("id", row.id);
      invalidateDocIndex();
      toast.success("Alias attached");
      await load();
    } catch (e: any) {
      toast.error(e?.message || "Failed");
    } finally {
      setBusy(null);
    }
  }

  async function dismiss(row: AuditRow) {
    setBusy(row.id);
    const { error } = await supabase.from("doc_link_audit" as any).update({ dismissed: true }).eq("id", row.id);
    setBusy(null);
    if (error) { toast.error(error.message); return; }
    setRows((r) => r.filter((x) => x.id !== row.id));
  }

  const counts = STATUS_ORDER.reduce((acc, s) => {
    acc[s] = rows.filter((r) => r.status === s).length;
    return acc;
  }, {} as Record<string, number>);

  const filtered = filter === "all" ? rows : rows.filter((r) => r.status === filter);

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-2xl font-serif">Link Health</h1>
          <p className="text-sm text-muted-foreground">
            Cross-doc references that couldn't resolve. Attach an alias once and the phrase resolves everywhere, forever.
          </p>
        </div>
        <Button onClick={runAudit} disabled={running}>
          {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Re-audit now
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setFilter("all")}
          className={`text-xs px-3 py-1.5 rounded border ${filter === "all" ? "bg-primary text-primary-foreground" : "bg-background"}`}
        >
          All ({rows.length})
        </button>
        {STATUS_ORDER.map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`text-xs px-3 py-1.5 rounded border ${filter === s ? "bg-primary text-primary-foreground" : "bg-background"}`}
          >
            {STATUS_LABEL[s]} ({counts[s] || 0})
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground">
          <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-emerald-600" />
          Nothing to review. Every reference in this category resolves cleanly.
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => {
            const source = docs.get(r.doc_id);
            return (
              <Card key={r.id} className="p-4 space-y-3">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <Badge variant="outline" className="font-mono">{r.doc_id}</Badge>
                  <Badge variant="secondary">{r.phrase_kind}</Badge>
                  <Badge
                    variant={r.status === "resolved" ? "outline" : "destructive"}
                    className={r.status === "ambiguous" ? "bg-amber-500/10 text-amber-800 border-amber-400" : ""}
                  >
                    {STATUS_LABEL[r.status] || r.status}
                  </Badge>
                  {source && (
                    <Link to={`/admin/library/${r.doc_id}`} className="text-muted-foreground hover:underline ml-auto truncate max-w-md">
                      {source.title}
                    </Link>
                  )}
                </div>

                <div className="text-sm">
                  <span className="font-mono bg-muted px-1.5 py-0.5 rounded">{r.phrase}</span>
                </div>

                {r.phrase_context && (
                  <div className="text-xs text-muted-foreground italic border-l-2 pl-2">
                    …{r.phrase_context}…
                  </div>
                )}

                {r.candidates && r.candidates.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-xs font-medium text-muted-foreground">Suggested targets:</div>
                    <div className="flex flex-wrap gap-2">
                      {r.candidates.map((c) => (
                        <Button
                          key={c.doc_id}
                          variant="outline"
                          size="sm"
                          disabled={busy === r.id}
                          onClick={() => attachAlias(r, c.doc_id)}
                          className="h-auto py-1.5 text-left justify-start"
                        >
                          <Link2 className="h-3 w-3 mr-1.5 flex-shrink-0" />
                          <span className="font-mono text-[10px] mr-1.5">{c.doc_id}</span>
                          <span className="truncate max-w-[220px]">{c.title}</span>
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-1">
                  <Button variant="ghost" size="sm" onClick={() => dismiss(r)} disabled={busy === r.id}>
                    <X className="h-3.5 w-3.5 mr-1" /> Leave unlinked
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
